import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Checkmark from "@ucs/Icons/Checkmark";
import Close from "@ucs/Icons/Close";
import ListSort from "@ucs/Icons/ListSort";
import Sliders from "@ucs/Icons/Sliders";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC, RefObject, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
    } = state;
    return {
        view: state.pages.productList.view,
        pagination: getProductListDataViewProperty(state, "pagination"),
        productFilters,
    };
};

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListColumnsStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
    closeButton?: InjectableCss;
    applyButtonSticky?: InjectableCss;
    applyFiltersButton?: InjectableCss;
    bestMatchStyles?: InjectableCss;
}

export const columnsStyles: ProductListColumnsStyles = {
    closeButton: {
        css: css`
            position: absolute;
            top: 5px;
            right: 5px;
            display: none;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                ]);
            }}
        `,
    },
    applyButtonSticky: {
        css: css`
            width: 100%;
            height: fit-content;
            position: sticky;
            bottom: 0px;
            left: 0px;
            right: 0px;
            display: none;
            z-index: 5;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                ]);
            }}
        `,
    },
    applyFiltersButton: {
        css: css`
            width: 100%;
            height: 48px;
        `,
    },
    bestMatchStyles: {
        css: css`
            padding: 0 5px !important;
            text-align: left;
            & svg {
                margin-top: 4px;
            }
        `,
    },
};

const styles = columnsStyles;
const smallerButtonsBreakpoint = "460px";

const Ellipse = styled.span`
    margin-top: -10px;
    margin-left: 8px;
    width: 8px;
    height: 8px;
    border: 1px solid #fff;
    border-radius: 50%;
    background-color: ${UCSColors.tertiaryEY00};
`;

const Container = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
`;
const leftContainerMobile = css`
    position: fixed;
    top: 100%;
    left: 0px;
    right: 0px;
    bottom: -100%;
    display: none;
    /* bottom: 0px; */
    /* padding-bottom: 50px; */
    transition: all 0.5s 0s ease;
    transition-property: top, bottom;
    z-index: 6;
    width: auto;
    overflow-y: auto;
    flex-direction: column;
    justify-content: space-between;
    z-index: 3001;
`;
const LeftContainer = styled.div`
    width: 380px;
    background-color: #fff;
    flex-shrink: 0;
    ${({ theme }) => {
        const { maxWidths } = theme.breakpoints;
        return breakpointMediaQueries(theme, [
            leftContainerMobile,
            leftContainerMobile,
            css`
                width: 300px;
            `,
        ]);
    }}
`;

const RightContainer = styled.div`
    align-self: stretch;
    padding: 15px;
    flex: 1 1;
    width: 100%;
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                padding: 15px;
            `,
            css`
                padding: 15px;
            `,
            css`
                padding: 10px;
            `,
        ]);
    }}
`;

const StickyFooter = styled.div`
    position: fixed;
    height: 50px;
    background-color: ${getColor("primary")};
    color: white;
    bottom: 0px;
    left: 0px;
    right: 0px;
    display: none;
    z-index: 5;
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                display: flex;
            `,
            css`
                display: flex;
            `,
        ]);
    }}

    button {
        font-size: 16px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-transform: uppercase;
        padding-top: 2px;
        @media only screen and (max-width: ${smallerButtonsBreakpoint}) {
            font-size: 14px;
        }

        span:first-child {
            width: 20px;
            height: 20px;
            margin-top: -4px;
            svg {
                height: 20px;
                width: 20px;
            }
            @media only screen and (max-width: ${smallerButtonsBreakpoint}) {
                width: 19px;
                margin-top: -5px;
            }
        }
        &:first-child {
            flex-basis: 50%;
        }
        &:last-child {
            flex-basis: 50%;
            border-left: 1px solid white;
            span:first-child {
                margin-top: -8px;
                width: 27px;
                height: 27px;
                svg {
                    width: 27px;
                    height: 27px;
                }
                @media only screen and (max-width: ${smallerButtonsBreakpoint}) {
                    width: 22px;
                    margin-right: 2px;
                }
            }
        }
    }
`;

const SortContainer = styled.div`
    ${leftContainerMobile}
    /* padding: 40px 30px; */
    background-color: #fff;
    font-size: 18px;
    ul {
        margin-top: 50px;
        li {
            width: 100%;
            padding: 15px 60px;
            border-top: 1px solid ${UCSColors.gray08};
            display: flex;
            flex-direction: row;
            align-items: center;
            &:last-child {
                border-bottom: 1px solid ${UCSColors.gray08};
            }
        }
    }
`;

const commonTypography = {
    size: 16,
    weight: 400,
    css: css`
        @media only screen and (max-width: ${smallerButtonsBreakpoint}) {
            font-size: 14px;
        }
    `,
};

const ProductListColumns: FC<Props> = ({ id, view, pagination, productFilters, addProductFilters }) => {
    const filterPanel: RefObject<HTMLDivElement> = React.createRef();
    const sortPanel: RefObject<HTMLDivElement> = React.createRef();
    const [filterOpened, setFilterOpened] = useState(filterPanel?.current?.style.top === "0%");
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [sortLabel, setSortLabel] = useState(translate("Best Match"));
    const filterLabel = translate("Filter");
    const sortOptionsItems: any[] = [];

    const onChangeSortHandler = (value: string, name: string) => {
        addProductFilters({ sort: value });
        setSortLabel(name);
    };

    const appliedFilters = () => {
        let applied = false;
        Object.entries(productFilters).forEach(([key, value]) => {
            if (key !== "pageCategoryId" && key !== "query" && key !== "sort" && value) {
                applied = true;
            }
        });
        return applied;
    };

    const getSortObjectByValue = (value?: string) => {
        return pagination ? pagination!.sortOptions.find(s => s.sortType === value) : null;
    };

    useEffect(() => {
        if (pagination) {
            const sort = getSortObjectByValue(pagination.sortType);
            if (sort) {
                setSortLabel(sort.displayName);
            }
        }
    }, []);

    useEffect(() => {
        setFiltersApplied(appliedFilters());
    }, [productFilters]);

    if (pagination) {
        const sortOptions = pagination.sortOptions;
        const onLiClick = (value: string, name: string) => {
            return () => {
                onChangeSortHandler(value, name);
            };
        };

        sortOptions.forEach(sortOption => {
            if (pagination.sortType === sortOption.sortType) {
                sortOptionsItems.push(
                    <li style={{ paddingLeft: "30px" }} key={sortOption.displayName}>
                        <Icon src={Checkmark} color="primary" size={20} style={{ marginRight: "10px" }}></Icon>
                        {sortOption.displayName}
                    </li>,
                );
            } else {
                sortOptionsItems.push(
                    <li key={sortOption.displayName} onClick={onLiClick(sortOption.sortType, sortOption.displayName)}>
                        {sortOption.displayName}
                    </li>,
                );
            }
        });
        sortOptionsItems.push();
    }

    const showFilter = () => {
        if (filterPanel && filterPanel.current) {
            filterPanel.current.style.display = "flex";
            setTimeout(() => {
                if (filterPanel && filterPanel.current) {
                    filterPanel.current.style.top = "0%";
                    filterPanel.current.style.bottom = "0%";
                }
            }, 1);
        }
    };

    const hideFilter = () => {
        const y = document!.getElementById("paginationTop")!.getBoundingClientRect().top + window.pageYOffset - 140;
        window.scrollTo({ top: y, behavior: "smooth" });
        if (filterPanel && filterPanel.current) {
            filterPanel.current.style.top = "100%";
            filterPanel.current.style.bottom = "-100%";
            setTimeout(() => {
                if (filterPanel && filterPanel.current) {
                    filterPanel.current.style.display = "none";
                }
            }, 500);
        }
    };

    const showSort = () => {
        if (sortPanel && sortPanel.current) {
            sortPanel.current.style.display = "flex";
            setTimeout(() => {
                if (sortPanel && sortPanel.current) {
                    sortPanel.current.style.top = "0%";
                    sortPanel.current.style.bottom = "0%";
                }
            }, 1);
        }
    };

    const hideSort = () => {
        if (sortPanel && sortPanel.current) {
            sortPanel.current.style.top = "100%";
            sortPanel.current.style.bottom = "-100%";
            setTimeout(() => {
                if (sortPanel && sortPanel.current) {
                    sortPanel.current.style.display = "none";
                }
            }, 500);
        }
    };

    const leftColumnGridItemStyle = view === "Table" ? styles.leftColumnTableViewGridItem : styles.leftColumnGridItem;
    const rightColumnGridItemStyle =
        view === "Table" ? styles.rightColumnTableViewGridItem : styles.rightColumnGridItem;
    return (
        <Container>
            <LeftContainer ref={filterPanel}>
                <StyledWrapper style={{ padding: "40px 30px" }}>
                    <Button color="transparent" {...styles.closeButton} onClick={hideFilter}>
                        <Icon src={Close} color="black"></Icon>
                    </Button>
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
                <StyledWrapper {...styles.applyButtonSticky}>
                    <Button
                        icon={{ src: Checkmark, position: "left" }}
                        typographyProps={{ transform: "uppercase", ...commonTypography }}
                        {...styles.applyFiltersButton}
                        onClick={hideFilter}
                    >
                        {translate("Apply Filters")}
                    </Button>
                </StyledWrapper>
            </LeftContainer>
            <RightContainer>
                <Hidden below="md">
                    <Zone contentId={id} zoneName="LocationFilter" />
                </Hidden>
                <Hidden above="sm" style={{ width: "100%" }}>
                    <Zone contentId={id} zoneName="LocationFilterMobile" />
                </Hidden>
                <Zone contentId={id} zoneName="Content01" />
            </RightContainer>
            {pagination && (
                <StickyFooter>
                    <Button
                        onClick={showFilter}
                        icon={{ src: Sliders, position: "left" }}
                        typographyProps={{
                            ...commonTypography,
                            lineHeight: 1.1,
                        }}
                    >
                        <span>{filterLabel}</span>
                        {filtersApplied && <Ellipse />}
                    </Button>
                    <Button
                        onClick={showSort}
                        icon={{ src: ListSort, position: "left" }}
                        typographyProps={{ ...commonTypography, lineHeight: 1.3 }}
                        {...styles.bestMatchStyles}
                    >
                        {sortLabel}
                    </Button>
                </StickyFooter>
            )}
            <SortContainer ref={sortPanel}>
                <StyledWrapper style={{ padding: "40px 30px" }}>
                    <Button color="transparent" {...styles.closeButton} onClick={hideSort}>
                        <Icon src={Close} color="black"></Icon>
                    </Button>
                    <Typography as="h4" variant="h4">
                        Sorting
                    </Typography>
                    <ul>{sortOptionsItems}</ul>
                </StyledWrapper>
                <StyledWrapper {...styles.applyButtonSticky}>
                    <Button
                        icon={{ src: Checkmark, position: "left" }}
                        typographyProps={{ transform: "uppercase", ...commonTypography }}
                        {...styles.applyFiltersButton}
                        onClick={hideSort}
                    >
                        {translate("Apply Sort")}
                    </Button>
                </StyledWrapper>
            </SortContainer>
        </Container>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListColumns),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
